import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { logger } from '../../utils/logger';

const FreeTrialRegistration = () => {
    const navigate = useNavigate();
    const [authStep, setAuthStep] = useState("email");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Password visibility toggles
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);


    // Token validation
    const [tokenValidated, setTokenValidated] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsContent, setTermsContent] = useState('');
    
    // Username validation
    const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });

    // Form data
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: '',
        company: '',
        phoneNumber: '',
        organizationType: '',
        organizationTypeOther: '',
        password: '',
        confirmPassword: '',
        businessSize: '',
        geographicAreaOfOperation: '',
    });
    const homepagePrimaryButtonClass =
        "inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white rounded-lg shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
    const homepageCtaButtonClass = `${homepagePrimaryButtonClass} px-8 py-3 text-base font-semibold`;
    const homepageFullWidthButtonClass = `${homepagePrimaryButtonClass} w-full px-6 py-2.5 text-sm font-semibold`;
    const homepageModalButtonClass = `${homepagePrimaryButtonClass} px-8 py-2.5 text-sm font-semibold`;

    // Auto-clear error messages after 5 seconds
    useEffect(() => {
        let errorTimer;
        if (error) {
            errorTimer = setTimeout(() => {
                setError('');
            }, 5000);
        }
        return () => {
            if (errorTimer) {
                clearTimeout(errorTimer);
            }
        }
    }, [error]);

    // Auto-clear success messages after 5 seconds
    useEffect(() => {
        let successTimer;
        if (success) {
            successTimer = setTimeout(() => {
                setSuccess('');
            }, 5000);
        }
        return () => {
            if (successTimer) {
                clearTimeout(successTimer);
            }
        }
    }, [success]);

    // Terms and conditions
    useEffect(() => {
        if (showTermsModal) {
            fetch('/terms-and-conditions.html')
                .then(response => response.text())
                .then(html => {
                    const sanitizedHtml = DOMPurify.sanitize(`<div>${html}</div>`);
                    setTermsContent(sanitizedHtml);
                    logger.log("Fetched and sanitized terms and conditions");
                })
                .catch((error) => {
                    logger.error('Error fetching terms', error);
                })
        }
    }, [showTermsModal]);

    const checkUsernameAvailability = async (username) => {
        if (!username || username.length < 3) {
            setUsernameStatus({ checking: false, available: false, message: 'Username must be at least 3 characters long' });
            return;
        }
        if (!username || username.length > 25) {
            setUsernameStatus({ checking: false, available: false, message: 'Username must be less than 25 characters long' });
            return;
        }

        setUsernameStatus({ checking: true, available: null, message: 'Checking username...' });

        try {
            logger.log('Checking username availability for:', username);
            logger.log('API endpoint:', API_ENDPOINTS.isUsernameAvailable);
            
            const response = await axios.post(API_ENDPOINTS.isUsernameAvailable, {
                username: username
            });
            
            logger.log('Username check response:', response.data);
            
            // Handle the nested response structure from backend
            const isAvailable = response.data.available?.username ?? response.data.all_available;
            const isTaken = response.data.taken?.username ?? false;
            
            logger.log('Username available:', isAvailable);
            logger.log('Username taken:', isTaken);
            
            if (isAvailable === true && isTaken === false) {
                setUsernameStatus({ 
                    checking: false, 
                    available: true, 
                    message: '✓ Username is available' 
                });
            } else if (isTaken === true) {
                setUsernameStatus({ 
                    checking: false, 
                    available: false, 
                    message: '✗ Username is already taken' 
                });
            } else {
                // Fallback for unexpected response
                logger.warn('Unexpected response format:', response.data);
                setUsernameStatus({ 
                    checking: false, 
                    available: null, 
                    message: 'Could not verify username - unexpected response format' 
                });
            }
        } catch (error) {
            logger.error('Username check error:', error);
            logger.error('Error response:', error.response);
            logger.error('Error message:', error.message);
            logger.error('Error status:', error.response?.status);
            logger.error('Error data:', error.response?.data);
            
            const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Could not verify username';
            
            setUsernameStatus({ 
                checking: false, 
                available: null, 
                message: errorMsg
            });
        }
    };

    const handleEmailSubmit = async () => {
        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(API_ENDPOINTS.getEmailCode, {
                email
            });
            setSuccess(response.data.message);
            setAuthStep("verify");
            setIsLoading(false);
            logger.log('Email registration response:', response.data);
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to send verification code. Please try again.';
            setError(errorMessage);
            logger.error('Email registration error:', error);
            setIsLoading(false);
        }
    };

    const handleVerificationSubmit = async () => {
        if (!verificationCode || verificationCode.length < 6) {
            setError('Please enter a valid verification code');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(API_ENDPOINTS.verifyCode, {
                email,
                code: verificationCode
            });
            setSuccess('Email verified successfully!');
            setAuthStep("signup");
            setIsLoading(false);
            logger.log('Verification response:', response.data);
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Invalid verification code. Please try again.';
            setError(errorMessage);
            logger.error('Verification error:', error);
            setIsLoading(false);
        }
    };


    const validatePassword = async (password, confirmPassword) => {
        if (!password || password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (!password.match(/[A-Z]/)) {
            return 'Password must contain at least 1 uppercase letter';
        }
        if (!password.match(/[a-z]/)) {
            return 'Password must contain at least 1 lowercase letter';
        }
        if (!password.match(/[0-9]/)) {
            return 'Password must contain at least 1 number';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSignupSubmit = async (e) => {
        if (e) {
            e.preventDefault(); // Prevent form submission and page refresh
        }
        
        // Validation
        if (!formData.firstName || !formData.lastName) {
            setError('First name and last name are required');
            return;
        }
        if (!formData.username || formData.username.length < 3) {
            setError('Username must be at least 3 characters long');
            return;
        }
        if (!formData.username || formData.username.length > 25) {
            setError('Username must be less than 25 characters long');
            return;
        }
        const passwordError = await validatePassword(formData.password, formData.confirmPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        // Check if username was verified and is available
        if (usernameStatus.available === false) {
            setError('Username is already taken. Please choose a different username.');
            return;
        }

        // If username hasn't been checked yet, check it now
        if (usernameStatus.available === null) {
            setIsLoading(true);
            try {
                const response = await axios.post(API_ENDPOINTS.isUsernameAvailable, {
                    username: formData.username
                });
                
                // Handle the nested response structure from backend
                const isAvailable = response.data.available?.username ?? response.data.all_available;
                const isTaken = response.data.taken?.username ?? false;
                
                if (isTaken === true || isAvailable === false) {
                    setError('Username is already taken. Please choose a different username.');
                    setUsernameStatus({ 
                        checking: false, 
                        available: false, 
                        message: '✗ Username is already taken' 
                    });
                    setIsLoading(false);
                    return;
                }
                setUsernameStatus({ 
                    checking: false, 
                    available: true, 
                    message: '✓ Username is available' 
                });
            } catch (error) {
                const errorMessage = error.response?.data?.error || 'Failed to check username. Please try again.';
                setError(errorMessage);
                logger.error('Username check error:', error);
                setIsLoading(false);
                return;
            }
            setIsLoading(false);
        }

        // Proceed to next step
        setAuthStep("optional");
    };


    const handleOptionalSubmit = async (e) => {
        if (e) {
            e.preventDefault(); // Prevent form submission and page refresh
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await axios.post(API_ENDPOINTS.registerFreeTrial, {
                email,
                verificationCode,
                ...formData
            });
            setSuccess('Account created successfully!');
            logger.log('Signup response:', response.data);
            setIsLoading(false);
            setAuthStep("complete");
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to create account. Please try again.';
            setError(errorMessage);
            logger.error('Signup error:', error);
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setIsLoading(true);
        setError(null);

        try {
            await axios.post(API_ENDPOINTS.resendVerificationCode, { email });
            setSuccess('Verification code resent! Please check your email.');
            setIsLoading(false);
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to resend code. Please try again.';
            setError(errorMessage);
            setIsLoading(false);
        }
    };

    const renderEmailStep = () => (
        <div className="w-full">
            <div className="flex flex-col items-center font-sans text-gray-800 mx-5 sm:mx-24 text-left">
                {/* <h1 className="text-2xl sm:text-3xl mb-6 font-bold text-center">South Coast AQMD Free Trial Promotion</h1> */}

                <div className="flex flex-row gap-4 mb-6 items-stretch">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded flex-[2] pr-2 flex flex-col justify-center">
                        <p className="text-base font-semibold text-blue-800 mb-2">
                            Free Access for South Coast AQMD Participants
                        </p>
                        <p className="text-sm text-blue-700">
                            Thanks to our partnership with the South Coast Air Quality Management District, AmpTrans is offering a complimentary Enterprise Plan for organizations conducting analyses within South Coast AQMD's jurisdiction. This plan provides full access to eFleetPlan for fleets to plan for electrification, and ChargerPlan for developers to determine suitable sites for charging stations.                    
                            Please note, however, that pursuant to Health and Safety Code Section 40730, South Coast AQMD does not endorse any product or service.
                        </p>
                    </div>
                    <div className="flex items-center justify-center flex-[1] w-full max-w-[350px] self-stretch">
                        <img 
                            src="/img/partner-logos/scaqmd-clearer.jpg" 
                            alt="SCAQMD Partnership" 
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>

                <p className="text-sm mb-4 text-center max-w-2xl">
                    Enter your email to get started. Free access is valid through April 15, 2027.
                </p>

                {error && (
                    <div className="w-full max-w-2xl mb-4">
                        <p className="text-red-600 text-sm border-2 border-red-500 rounded-lg p-3 bg-red-50">
                            {error}
                        </p>
                    </div>
                )}

                {success && (
                    <div className="w-full max-w-2xl mb-4">
                        <p className="text-green-600 text-sm border-2 border-green-500 rounded-lg p-3 bg-green-50">
                            {success}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex flex-col items-center mt-6 space-y-4">
                <input
                    id="email"
                    type="email"
                    placeholder="Business Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
                    className="w-4/5 sm:w-1/2 p-3 rounded-lg border-2 border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
                    disabled={isLoading}
                    autoComplete="email"
                />
                <button
                    className={homepageCtaButtonClass}
                    onClick={handleEmailSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? "Sending..." : "Continue"}
                </button>

                <p className="text-xs text-center text-gray-600 max-w-2xl">
                    Your information will only be used for account setup and access. For questions, contact <a href="mailto:info@amptrans.net" className="text-blue-600 font-sans underline cursor-pointer hover:text-blue-700">info@amptrans.net</a>.
                </p>

                <p className="text-xs text-gray-600 mt-2">
                    Already have an account? <a href="/login" className="text-blue-600 hover:underline">Sign in</a>
                </p>
            </div>
        </div>
    );

    const renderVerifyStep = () => (
        <div className="w-full">
            <div className="flex flex-col items-center font-sans text-gray-800 mx-5 sm:mx-24 text-center">
                <h1 className="text-xl sm:text-2xl mb-6 font-bold">Verify Your Email</h1>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded max-w-2xl">
                    <p className="text-sm text-blue-700">
                        We've sent a verification code to <strong>{email}</strong>
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                        Please check your email and enter the code below.
                    </p>
                </div>

                {error && (
                    <div className="w-full max-w-2xl mb-4">
                        <p className="text-red-600 text-sm border-2 border-red-500 rounded-lg p-3 bg-red-50">
                            {error}
                        </p>
                    </div>
                )}

                {success && (
                    <div className="w-full max-w-2xl mb-4">
                        <p className="text-green-600 text-sm border-2 border-green-500 rounded-lg p-3 bg-green-50">
                            {success}
                        </p>
                    </div>
                )}

                <div className="flex flex-col items-center mt-6 space-y-4 w-full">
                    {/* 6-digit code input with individual boxes */}
                    <div className="flex gap-2 sm:gap-3 justify-center">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                            <div key={index} className="relative">
                                <input
                                    ref={(el) => {
                                        if (!window.codeInputRefs) window.codeInputRefs = [];
                                        window.codeInputRefs[index] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={verificationCode[index] || ''}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value) {
                                            const newCode = verificationCode.split('');
                                            newCode[index] = value;
                                            const updatedCode = newCode.join('');
                                            setVerificationCode(updatedCode);

                                            // Auto-focus next input
                                            if (index < 5 && window.codeInputRefs[index + 1]) {
                                                window.codeInputRefs[index + 1].focus();
                                            }

                                            // Auto-verify when all 6 digits are entered
                                            // if (updatedCode.length === 6) {
                                            //     setTimeout(() => {
                                            //         handleVerificationSubmit();
                                            //     }, 100);
                                            // }
                                        } else {
                                            const newCode = verificationCode.split('');
                                            newCode[index] = '';
                                            setVerificationCode(newCode.join(''));
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        // Handle backspace
                                        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
                                            window.codeInputRefs[index - 1].focus();
                                        }
                                        // Handle paste
                                        if (e.key === 'v' && (e.metaKey || e.ctrlKey)) {
                                            e.preventDefault();
                                            navigator.clipboard.readText().then(text => {
                                                const digits = text.replace(/\D/g, '').slice(0, 6);
                                                setVerificationCode(digits);
                                            });
                                        }
                                    }}
                                    className="w-12 h-14 sm:w-14 sm:h-16 text-center text-xl font-bold border-b-4 border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent transition-colors duration-200 rounded-sm"
                                    disabled={isLoading}
                                    autoFocus={index === 0}
                                />
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                        Enter the 6-digit code sent to your email
                    </p>

                    <button
                        className={homepageCtaButtonClass}
                        onClick={handleVerificationSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? "Verifying..." : "Verify Code"}
                    </button>

                    <div className="flex flex-col items-center space-y-2 mt-4">
                        <p className="text-xs text-gray-600">
                            Didn't receive the code?
                        </p>
                        <button
                            className="text-blue-600 hover:underline text-xs"
                            onClick={handleResendCode}
                            disabled={isLoading}
                        >
                            Resend Code
                        </button>
                        <button
                            className="text-blue-600 hover:underline text-xs"
                            onClick={() => setAuthStep("email")}
                        >
                            Change Email
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSignupStep = () => (
        <div className="w-full">
            <div className="flex flex-col items-center font-sans text-gray-800 mx-5 sm:mx-24">
                <h1 className="text-xl sm:text-2xl mb-6 font-bold text-center">Complete Your Registration</h1>

                <p className="text-sm text-gray-600 mb-6 text-center max-w-2xl">
                    Just a few more details to set up your free trial account.
                </p>
                <form onSubmit={handleSignupSubmit} method="post" action="/api/register-trial" className="space-y-2 w-full max-w-2xl">
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-center">
                            <label htmlFor="trial-first-name" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                                First Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                                id="trial-first-name"
                                name="first-name"
                                value={formData.firstName || ''}
                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                required
                                disabled={isLoading}
                                autoComplete="given-name"
                            />
                        </div>
                        <div className="text-center">
                            <label htmlFor="trial-last-name" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                                Last Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                                id="trial-last-name"
                                name="last-name"
                                value={formData.lastName || ''}
                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                required
                                disabled={isLoading}
                                autoComplete="family-name"
                            />
                        </div>
                    </div>

                    {/* Username */}
                    <div className="text-center">
                        <label htmlFor="trial-username" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Username <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                className={`w-full px-3 py-2 rounded-lg bg-gray-100 border-2 font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs ${
                                    usernameStatus.available === true ? 'border-green-500' : 
                                    usernameStatus.available === false ? 'border-red-500' : 
                                    'border-transparent'
                                }`}
                                id="trial-username"
                                name="username"
                                value={formData.username || ''}
                                onChange={(e) => {
                                    setFormData({ ...formData, username: e.target.value });
                                    // Reset status when user types
                                    if (usernameStatus.available !== null) {
                                        setUsernameStatus({ checking: false, available: null, message: '' });
                                    }
                                }}
                                onBlur={(e) => checkUsernameAvailability(e.target.value)}
                                required
                                disabled={isLoading}
                                autoComplete="username"
                            />
                            {usernameStatus.checking && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                </div>
                            )}
                        </div>
                        {usernameStatus.message && (
                            <div className={`text-xs mt-1 text-left font-sans ${
                                usernameStatus.available === true ? 'text-green-600' : 
                                usernameStatus.available === false ? 'text-red-600' : 
                                'text-gray-600'
                            }`}>
                                {usernameStatus.message}
                            </div>
                        )}
                    </div>

                    {/* Password Fields */}
                    <div className="">
                        <label htmlFor="trial-password" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Password <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="w-full px-3 py-2 pr-10 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                                id="trial-password"
                                name="password"
                                value={formData.password || ''}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                disabled={isLoading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                disabled={isLoading}
                            >
                                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 font-sans">
                            Password must contain at least 8 characters, 1 uppercase letter, 1 lowercase letter, and 1 number.
                        </div>
                    </div>

                    <div className="text-center">
                        <label htmlFor="trial-confirm-password" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Confirm Password <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                className="w-full px-3 py-2 pr-10 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                                id="trial-confirm-password"
                                name="confirm-password"
                                value={formData.confirmPassword || ''}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                required
                                disabled={isLoading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                disabled={isLoading}
                            >
                                {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Required Fields Note - Centered */}
                    <div className="text-center text-xs text-gray-500 mt-2">
                        <span className="text-red-600 font-bold">*</span> Required fields
                    </div>

                    {/* Terms and Conditions - Centered */}
                    {/* <div className="flex justify-center items-center mt-2">
                        <label htmlFor="terms-and-conditions" className="flex items-center text-center">
                            <input
                                type="checkbox"
                                id="terms-and-conditions"
                                onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.checked })}
                                className="mr-2"
                                disabled={isLoading}
                            />
                            <span className="font-sans text-sm">I agree to the{' '}</span>
                            <span
                                onClick={() => setShowTermsModal(true)}
                                className="text-blue-600 font-sans underline cursor-pointer hover:text-blue-700 text-sm ml-1"
                            >
                                Terms and Conditions
                            </span>
                        </label>
                    </div> */}

                    {/* Terms Modal */}
                    {showTermsModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                                {/* Modal Header */}
                                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-800 font-sans">Terms and Conditions</h3>
                                    <button
                                        onClick={() => setShowTermsModal(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>

                                {/* Modal Body - Scrollable Content */}
                                <div className="flex-1 overflow-y-auto p-6">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: termsContent }}
                                        className="prose prose-sm max-w-none font-sans"
                                    />
                                </div>

                                {/* Modal Footer */}
                                <div className="flex justify-center p-6 border-t border-gray-200 bg-gray-50">
                                    <button
                                        onClick={() => setShowTermsModal(false)}
                                        className={homepageModalButtonClass}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button - Centered */}
                    <div className="flex justify-center mt-3">
                        <button
                            type="submit"
                            className={homepageFullWidthButtonClass}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    ...Loading
                                </>
                            ) : (
                                'Next Step'
                            )}
                        </button>
                    </div>
                </form>

                {/* Error Message - Centered */}
                {error && (
                    <div className="text-center mt-2">
                        <p className="text-red-600 text-xs font-sans">{error}</p>
                    </div>
                )}
            </div>
        </div>
    );


    const renderOptionalFields = () => (
        <div className="w-full">
            <div className="flex flex-col items-center font-sans text-gray-800 mx-5 sm:mx-24">
                <h1 className="text-xl sm:text-2xl mb-6 font-bold text-center">Complete Your Registration</h1>

                <p className="text-sm text-gray-600 mb-6 text-center max-w-2xl">
                    Just a few more details to set up your free trial account.
                </p>
                <form onSubmit={handleOptionalSubmit} method="post" action="/api/complete-trial-registration" className="space-y-2 w-full max-w-2xl">
                    {/* Company */}
                    <div className="text-center">
                        <label htmlFor="trial-company" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Company <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                            id="trial-company"
                            name="company"
                            value={formData.company || ''}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            disabled={isLoading}
                            required
                            autoComplete="organization"
                        />
                    </div>
                    <div className="text-center">
                        <label htmlFor="trial-business-size" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Business Size
                        </label>
                        <div className="relative w-full">
                            <select
                                className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs appearance-none cursor-pointer"
                                id="trial-business-size"
                                name="business-size"
                                value={formData.businessSize || ''}
                                onChange={(e) => setFormData({ ...formData, businessSize: e.target.value })}
                                disabled={isLoading}
                            >
                                <option value="">Select business size</option>
                                <option value="1-10">1-10 employees</option>
                                <option value="11-50">11-50 employees</option>
                                <option value="51-200">51-200 employees</option>
                                <option value="201-500">201-500 employees</option>
                                <option value="501-1000">501-1,000 employees</option>
                                <option value="1000+">1,000+ employees</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="text-center">
                        <label htmlFor="trial-role" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Role / Title
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                            id="trial-role"
                            name="role"
                            value={formData.role || ''}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            disabled={isLoading}
                            autoComplete="organization-title"
                        />
                    </div>

                    <div className="text-center">
                        <label htmlFor="trial-organization-type" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Organization Type   
                        </label>
                        <select
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                            id="trial-organization-type"
                            name="organization-type"
                            value={formData.organizationType || ''}
                            onChange={(e) => setFormData({ ...formData, organizationType: e.target.value })}
                            disabled={isLoading}
                        >
                            <option value="">Select organization type</option>
                            <option value="fleet">Fleet</option>
                            <option value="developer">Developer</option>
                            <option value="utility">Utility</option>
                            <option value="oem">OEM</option>
                            <option value="dealership">Dealership</option>
                            <option value="regulatory">Regulator</option>
                            <option value="other">Other (please specify)</option>
                        </select>
                        {formData.organizationType === "other" && (
                            <div className="text-center mt-3">
                                <label htmlFor="organization-type-other" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                                    Please Specify <span className="text-red-600">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                                    id="organization-type-other"
                                    value={formData.organizationTypeOther || ''}
                                    onChange={(e) => setFormData({ ...formData, organizationTypeOther: e.target.value })}
                                    disabled={isLoading}
                                    required
                                    placeholder="Please specify your organization type"
                                />
                            </div>
                        )}
                    </div>

                    {/* <div className="text-center">
                        <label htmlFor="geographic-area-of-operation" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Geographic Area of Operation
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                            id="geographic-area-of-operation"
                            onChange={(e) => setFormData({ ...formData, geographicAreaOfOperation: e.target.value })}
                            disabled={isLoading}
                        />
                    </div> */}

                    <div className="text-center">
                        <label htmlFor="trial-phone-number" className="block mb-1 text-gray-800 font-sans font-medium text-xs text-left">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-sans focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-xs"
                            id="trial-phone-number"
                            name="phone-number"
                            value={formData.phoneNumber || ''}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                            disabled={isLoading}
                            autoComplete="tel"
                        />
                    </div>

                    {/* Required Fields Note - Centered */}
                    <div className="text-center text-xs text-gray-500 mt-2">
                        <span className="text-red-600 font-bold">*</span> Required fields
                    </div>

                    {/* Terms and Conditions - Centered */}
                    <div className="flex justify-center items-center mt-2">
                        <label htmlFor="terms-and-conditions" className="flex items-center text-center">
                            <input
                                type="checkbox"
                                id="terms-and-conditions"
                                onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.checked })}
                                className="mr-2"
                                disabled={isLoading}
                            />
                            <span className="font-sans text-xs">I agree to the{' '}</span>
                            <span
                                onClick={() => setShowTermsModal(true)}
                                className="text-blue-600 font-sans underline cursor-pointer hover:text-blue-700 text-xs ml-1"
                            >
                                Terms and Conditions
                            </span>
                        </label>
                    </div>

                    {/* Terms Modal */}
                    {showTermsModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                                {/* Modal Header */}
                                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-800 font-sans">Terms and Conditions</h3>
                                    <button
                                        onClick={() => setShowTermsModal(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>

                                {/* Modal Body - Scrollable Content */}
                                <div className="flex-1 overflow-y-auto p-6">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: termsContent }}
                                        className="prose prose-sm max-w-none font-sans"
                                    />
                                </div>

                                {/* Modal Footer */}
                                <div className="flex justify-center p-6 border-t border-gray-200 bg-gray-50">
                                    <button
                                        onClick={() => setShowTermsModal(false)}
                                        className={homepageModalButtonClass}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button - Centered */}
                    <div className="flex justify-center mt-3">
                        <button
                            type="submit"
                            className={homepageFullWidthButtonClass}
                            disabled={!formData.termsAndConditions || isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </div>
                </form>

                {/* Error Message - Centered */}
                {error && (
                    <div className="text-center mt-2">
                        <p className="text-red-600 text-xs font-sans">{error}</p>
                    </div>
                )}
            </div>
        </div>
    )

    const renderCompleteStep = () => (
        <div className="w-full">
            <div className="flex flex-col items-center font-sans text-gray-800 mx-5 sm:mx-24">
                <h2 className="text-xl font-bold text-gray-800 font-sans mb-4">Thank you for registering! </h2>
                <p className="text-gray-600 font-sans">Welcome to the AmpTrans suite. You are registered with an enterprise subscription and have full access to all of our tools. <a href="/login" className="text-blue-600 font-sans underline cursor-pointer hover:text-blue-700">Login</a> to your account to check it out!</p>
            </div>
        </div>
    )
    // Main render
    return (
        <div className="min-h-screen bg-gray-50 font-sans flex items-center justify-center overflow-hidden pt-20 pb-8">
            <div className="container mx-auto px-4 h-full flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-h-[90vh] overflow-y-auto w-full max-w-4xl">
                    {/* Step Content */}
                    {authStep === "email" && renderEmailStep()}
                    {authStep === "verify" && renderVerifyStep()}
                    {authStep === "signup" && renderSignupStep()}
                    {authStep === "optional" && renderOptionalFields()}
                    {authStep === "complete" && renderCompleteStep()}
                </div>
            </div>
        </div>
    );
};

export default FreeTrialRegistration;
